import React, { useEffect, useState } from "react";
import { req, L } from "./api";
import { BiText, Field, Check, Msg } from "./fields";

export function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => { req("/admin/settings").then((x) => setS({ stats: [], ...x })).catch((e) => setMsg(e.message)); }, []);
  if (!s) return <p className="muted">Loading…</p>;

  const up = (k, v) => setS({ ...s, [k]: v });
  const upStat = (i, k, v) => { const st = [...s.stats]; st[i] = { ...st[i], [k]: v }; up("stats", st); };
  const save = async (e) => {
    e.preventDefault();
    try { await req("/admin/settings", { method: "PUT", body: s }); setMsg("Saved."); }
    catch (x) { setMsg("Could not save: " + x.message); }
  };

  return (
    <form className="form" onSubmit={save} data-testid="settings-form">
      <Msg msg={msg} />
      <div className="grp">Contact</div>
      <Field label="Mobile number (as shown)"><input value={s.phone || ""} onChange={(e) => up("phone", e.target.value)} /></Field>
      <Field label="WhatsApp number (digits only)"><input value={s.whatsapp || ""} onChange={(e) => up("whatsapp", e.target.value)} /></Field>
      <Field label="Email"><input value={s.email || ""} onChange={(e) => up("email", e.target.value)} /></Field>
      <Field label="LinkedIn profile URL"><input value={s.linkedin || ""} onChange={(e) => up("linkedin", e.target.value)} data-testid="settings-linkedin" /></Field>
      <BiText label="Office hours" value={s.hours} onChange={(v) => up("hours", v)} />
      <div className="grp">Chairman</div>
      <BiText label="Name" value={s.chairName} onChange={(v) => up("chairName", v)} />
      <BiText label="Title" value={s.chairTitle} onChange={(v) => up("chairTitle", v)} />
      <BiText label="Credentials line" value={s.chairCreds} onChange={(v) => up("chairCreds", v)} />
      <BiText label="Biography (full — About page)" value={s.chairBio} onChange={(v) => up("chairBio", v)} area />
      <BiText label="Short biography (press, speaker cards)" value={s.chairBioShort} onChange={(v) => up("chairBioShort", v)} area />
      <BiText label="Chairman's message" value={s.chairMessage} onChange={(v) => up("chairMessage", v)} area />
      <div className="grp">Key figures (home page band)</div>
      {(s.stats || []).map((f, i) => (
        <React.Fragment key={i}>
          <BiText label={`Figure ${i + 1} — number`} value={f.value} onChange={(v) => upStat(i, "value", v)} />
          <BiText label={`Figure ${i + 1} — label`} value={f.label} onChange={(v) => upStat(i, "label", v)} />
          <div className="row-actions" style={{ gridColumn: "1/-1" }}>
            <button type="button" className="btn sm ghost danger" onClick={() => up("stats", s.stats.filter((_, j) => j !== i))}>Remove figure {i + 1}</button>
          </div>
        </React.Fragment>))}
      <div className="row-actions" style={{ gridColumn: "1/-1" }}>
        <button type="button" className="btn sm ghost" onClick={() => up("stats", [...(s.stats || []), { value: L(), label: L() }])} data-testid="stats-add">Add a figure</button>
      </div>
      <Check label="Show the note that these figures are illustrative" checked={!!s.statsIllustrative} onChange={(v) => up("statsIllustrative", v)} testid="stats-illustrative" />
      <div className="grp">Meeting holidays / blocked dates</div>
      <Field label="Dates with no meetings (one per line, YYYY-MM-DD)" full>
        <textarea rows={3} value={(s.holidays || []).join("\n")} onChange={(e) => up("holidays", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} />
      </Field>
      <div className="row-actions" style={{ gridColumn: "1/-1" }}>
        <button type="submit" className="btn" data-testid="settings-save">Save settings</button>
      </div>
    </form>
  );
}

const ROLES = [["super_admin", "Super admin — everything"], ["editor", "Editor — content, media, documents"], ["events", "Events manager — events and bookings"], ["enquiries", "Enquiries handler — inbox and bookings"], ["viewer", "Viewer — read only"]];

export function UsersAdmin({ me }) {
  const [items, setItems] = useState(null);
  const [audit, setAudit] = useState([]);
  const [n, setN] = useState({ name: "", email: "", password: "", role: "editor" });
  const [msg, setMsg] = useState("");

  const load = () => {
    req("/admin/users").then(setItems).catch((e) => setMsg(e.message));
    req("/admin/audit").then(setAudit).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await req("/admin/users", { method: "POST", body: n });
      setMsg("User created."); setN({ name: "", email: "", password: "", role: "editor" }); load();
    } catch (x) { setMsg("Could not create: " + x.message); }
  };
  const del = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await req(`/admin/users/${u.id}`, { method: "DELETE" }); load(); } catch (x) { setMsg(x.message); }
  };

  if (!items) return <p className="muted">Loading…</p>;
  return (
    <div className="stack" style={{ gap: 32 }}>
      <Msg msg={msg} />
      <form className="form" onSubmit={create} data-testid="user-create-form">
        <div className="grp">Add a user</div>
        <Field label="Name"><input value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} data-testid="user-name" /></Field>
        <Field label="Email"><input type="email" value={n.email} onChange={(e) => setN({ ...n, email: e.target.value })} required data-testid="user-email" /></Field>
        <Field label="Password"><input type="text" value={n.password} onChange={(e) => setN({ ...n, password: e.target.value })} required minLength={8} data-testid="user-password" /></Field>
        <Field label="Role"><select value={n.role} onChange={(e) => setN({ ...n, role: e.target.value })} data-testid="user-role">{ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        <div className="row-actions" style={{ gridColumn: "1/-1" }}><button className="btn" type="submit" data-testid="user-create">Create user</button></div>
      </form>
      <div className="tbl-wrap"><table className="tbl" data-testid="users-table">
        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Last sign-in</th><th></th></tr></thead>
        <tbody>{items.map((u) => (
          <tr key={u.id}><td>{u.name}</td><td>{u.email}</td><td><span className="pill">{u.role}</span></td>
            <td className="num">{u.lastLogin ? new Date(u.lastLogin).toLocaleString("en-GB") : "—"}</td>
            <td>{u.id !== me.id && <button className="btn sm ghost danger" onClick={() => del(u)}>Remove</button>}</td></tr>))}</tbody></table></div>
      {audit.length ? (
        <div className="stack" style={{ gap: 10 }}>
          <span className="eyebrow">Audit log</span>
          <ul className="muted small" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {audit.map((a) => <li key={a.id}><span className="num">{new Date(a.at).toLocaleString("en-GB")}</span> — {a.user} {a.action} {a.collection}/{a.item}</li>)}
          </ul>
        </div>) : null}
    </div>
  );
}
